import { useEffect, useMemo, useRef, useState } from "react";
import { Identifier, useGetList, useGetMany, useGetOne } from "react-admin";
import {
  Claimant,
  ClaimsSummaryRow,
  Reimbursement,
  ResourceName,
  StoplossClaim,
  Vendor,
  WatchListRow,
  type Client,
  type Policy,
} from "@/types";
import { calculateClaimsSummaryRows } from "./utils/calculateClaimsSummaryRows";
import { calculateWatchListRows } from "./utils/calculateWatchListRows";
import { isClaimInReportingPeriod } from "./utils/reportingPeriod";

// Stable empty-array reference for useGetList/useGetMany defaults. Without this, a
// `= []` literal default mints a NEW array every render while a query is loading
// (data is undefined), and any effect that depends on it re-runs every render — the
// root cause of the #5168 / #2293 render loop. One shared constant keeps the reference
// stable so effect deps don't churn during loading.
const EMPTY_ARRAY: never[] = [];

// ==================== TYPES ====================

interface UseClaimsSummaryDataParams {
  clientId?: Identifier;
  /**
   * The selected policy's ID, not the Policy object (#7306).
   *
   * This hook already owns the `policies` query, so it derives the object here and
   * returns it. The caller used to hold a Policy object in state and pass it back
   * down, which made that copy the de-facto source of truth for everything read off
   * the policy — including `asd_tracking`, which the ASD engine rewrites on every
   * document load. The banner then showed a corridor snapshot from whenever the tab
   * mounted.
   */
  selectedPolicyId?: Identifier;
  startDate: string;
  endDate: string;
}

/** A feeding fetch that returned as many rows as it asked for — i.e. probably more exist. */
export interface TruncatedSource {
  label: string;
  fetched: number;
  total: number;
}

// Page sizes for the three client-scoped fetches that feed the exhibit. Named because
// the truncation check below compares the server's `total` against the SAME number —
// bumping a request without bumping its guard is how a silently short report ships.
// None of these endpoints clamps server-side, so the request is honoured as asked.
const CLAIMANTS_PAGE_SIZE = 5000;
const STOPLOSS_CLAIMS_PAGE_SIZE = 5000;
// Lifted 1000 -> 5000 to match its siblings (review thread 27748). This is the least
// filtered of the three — no reporting-period filter is applied server-side — and it
// feeds Variance, a dollar column on the client-facing exhibit, so it was the cap most
// likely to trip first and the one whose failure lands on money rather than population.
// #5166 already bit this codebase on a 1000-row default.
const REIMBURSEMENTS_PAGE_SIZE = 5000;

interface UseClaimsSummaryDataResult {
  /** All policies for the client (for policy dropdown) */
  policies: Policy[];
  policiesLoading: boolean;

  /**
   * The selected policy, resolved from the LIVE `policies` result rather than from a
   * caller-held copy, so anything read off it (notably `asd_tracking`) reflects the
   * latest fetch (#7306). Undefined until the list loads or if the id is not in it.
   */
  selectedPolicy?: Policy;

  /** Carrier vendor map keyed by vendor ID (for policy dropdown labels) */
  carrierMap: Record<string, Vendor>;

  /** Claimants at or over their effective specific deductible (claimant grain) */
  summaryRows: ClaimsSummaryRow[];

  /**
   * claimant_id_ref -> claimant_name for every claimant fetched for this client.
   *
   * Deliberately NOT derived from `summaryRows`. #6828 made the Summary population
   * (spend-vs-deductible) independent of the Claims Detail population (claims created
   * in the period), so a `summaryRows`-derived map is no longer total over the claims
   * on the report and renders below-deductible claimants as "Unknown" (review threads
   * 27744 / 27746). `claimants` is the set `summaryRows` is derived FROM, so it covers
   * both populations.
   */
  claimantNameById: Map<string, string>;

  /** Claimants at 50–99% of deductible — uses backend snapshot totals */
  watchListRows: WatchListRow[];

  /** Stop-loss claims created within the selected date range (claim grain — detail table + export) */
  stoplossClaims: StoplossClaim[];
  stoplossClaimsLoading: boolean;

  /** Reimbursements for the client — feeds summary totals and the reimbursement export tab (#5310) */
  reimbursements: Reimbursement[];
  /** stoploss_claim_id_ref → Aegle Accel funded date (aura_disbursed_at) for the reimbursement exhibit */
  fundedDateByClaimId: Record<string, string>;

  /**
   * Non-empty when any feeding fetch hit its page size, so the exhibit is built from a
   * PARTIAL data set (#6823 review). The report is client-facing and the period filter
   * runs in the browser, so a short fetch silently omits rows rather than erroring —
   * this is what lets the surface say so instead.
   */
  truncatedSources: TruncatedSource[];

  /** Export dependencies */
  carrierVendor?: Vendor;
  tpaVendors: Vendor[];
  rxVendors: Vendor[];
  policyholderClient?: Client;

  /** Combined loading state for all shared data (claimants, claim items, reimbursements, stoploss claims) */
  sharedDataLoading: boolean;
  claimantsLoading: boolean;
}

// ==================== HOOK ====================

export const useClaimsSummaryData = ({
  clientId,
  selectedPolicyId,
  startDate,
  endDate,
}: UseClaimsSummaryDataParams): UseClaimsSummaryDataResult => {
  const [summaryRows, setSummaryRows] = useState<ClaimsSummaryRow[]>([]);
  const [watchListRows, setWatchListRows] = useState<WatchListRow[]>([]);

  // ==================== POLICY FETCHES ====================

  const { data: policies = EMPTY_ARRAY, isLoading: policiesLoading } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "effective_start_date", order: "DESC" },
      meta: { expand: ["specific_deductible"] },
    },
    { enabled: !!clientId }
  );

  // Derived from the live query above, so a refetch propagates (#7306). Everything
  // downstream reads the policy through this, which is why the ASD corridor banner
  // now tracks `asd_tracking` instead of a mount-time copy of it.
  const selectedPolicy = useMemo(
    () => (selectedPolicyId === undefined ? undefined : policies.find((p) => p.id === selectedPolicyId)),
    [policies, selectedPolicyId]
  );

  const policyId = selectedPolicy?.id;
  const specificDeductible = selectedPolicy?.specific_benefit?.specific_deductible ?? 0;

  // Carrier names for policy dropdown labels
  const carrierIds = useMemo(
    () => [...new Set(policies.map((p) => p.vendor_id_ref).filter((id): id is string => !!id))],
    [policies]
  );

  const { data: carriers = EMPTY_ARRAY } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: carrierIds, meta: { "X-Fields": "id,company_name" } },
    { enabled: carrierIds.length > 0 }
  );

  const carrierMap = useMemo(
    () => carriers.reduce<Record<string, Vendor>>((acc, c) => ({ ...acc, [c.id]: c }), {}),
    [carriers]
  );

  // ==================== SHARED DATA FETCHES ====================
  // Claimants + claim items feed the watch list; claimants + stoploss claims + reimbursements feed the summary

  const {
    data: claimants = EMPTY_ARRAY,
    total: claimantsTotal,
    isLoading: claimantsLoading,
  } = useGetList<Claimant>(
    ResourceName.CLAIMANTS,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: CLAIMANTS_PAGE_SIZE },
      sort: { field: "claimant_name", order: "ASC" },
      meta: { "X-Fields": "id,claimant_name,member_id,policy_data" },
    },
    { enabled: !!clientId && !!policyId }
  );

  const {
    data: reimbursements = EMPTY_ARRAY,
    total: reimbursementsTotal,
    isLoading: reimbursementsLoading,
  } = useGetList<Reimbursement>(
    ResourceName.REIMBURSEMENTS,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: REIMBURSEMENTS_PAGE_SIZE },
      meta: {
        // stoploss_claim_id_ref joins to the funded date (aura_disbursed_at). payment_method
        // + payment_date are the authoritative payment fields for the reimbursement tab (#5310);
        // the legacy check_date/eft/check_number are kept only as a fallback for older rows.
        // received_date is the last fallback of `reimbursementDate` (#7560) — it MUST stay in
        // this projection, or rows dated only by it fall out of the Summary.
        "X-Fields":
          "id,claimant_id_ref,stoploss_claim_id_ref,payment_method,payment_date,check_date,received_date,requested_amount,reimbursement_amount,pended_amount,denial_amount,eft,check_number",
      },
    },
    { enabled: !!clientId && !!policyId }
  );

  const {
    data: stoplossClaimsFetched = EMPTY_ARRAY,
    total: stoplossClaimsTotal,
    isLoading: stoplossClaimsLoading,
  } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      filter: { client_id_ref: clientId, policy_id_ref: policyId },
      pagination: { page: 1, perPage: STOPLOSS_CLAIMS_PAGE_SIZE },
      // Sorted on created_datetime to match the reporting-period axis (#6823).
      sort: { field: "created_datetime", order: "DESC" },
      meta: {
        // created_datetime scopes the reporting period (#6823) — it MUST stay in this
        // projection. Without it the field is absent from the response and every claim
        // falls outside the period, emptying the report.
        "X-Fields":
          "id,claimant_id_ref,merged_into_claimant_id_ref,stoploss_claim_type,claim_status,created_datetime,submitted_date,first_service_date,last_service_date,first_paid_date,last_paid_date,total_billed_amount,total_paid_amount,notes,aura_disbursed_at,vendor_id_ref",
      },
    },
    { enabled: !!clientId && !!policyId }
  );

  // Attribute each claim to its LIVE claimant (#7244 safety net -- the merge
  // moves every claim). If a merged, retired record still owns one, its
  // claimant_id_ref names a deleted claimant the report never fetches, so it
  // would group under nobody. The API resolves the survivor
  // (merged_into_claimant_id_ref); normalizing here, once, keeps the Summary
  // rows, Claims Detail, the exhibit and the export in agreement. Safe for a
  // read-only report: the only write (ClaimNoteCell) PATCHes { notes } alone.
  const stoplossClaimsRaw = useMemo(
    () =>
      stoplossClaimsFetched.some((c) => c.merged_into_claimant_id_ref)
        ? stoplossClaimsFetched.map((c) =>
            c.merged_into_claimant_id_ref ? { ...c, claimant_id_ref: c.merged_into_claimant_id_ref } : c
          )
        : stoplossClaimsFetched,
    [stoplossClaimsFetched]
  );

  const sharedDataLoading = claimantsLoading || reimbursementsLoading || stoplossClaimsLoading;

  // ==================== STOPLOSS CLAIMS (DETAIL TABLE) ====================
  // Claim-grain: the stop-loss claims CREATED in the reporting period (#6823), for the
  // Claims Detail table and the export's Claims Detail sheet.
  //
  // This deliberately answers a different question from the Summary table, which is
  // claimant-grain and populated by spend-vs-deductible. So it is NOT gated on the
  // Summary population: a claim filed in the period is listed even if its claimant is
  // absent from the Summary (e.g. a stale policy_data snapshot puts their YTD under
  // the deductible). Gating it would silently hide real filed claims — the failure
  // mode #6825 was about, in a new guise.
  //
  // Consequence, by design: the Summary's Owed column and the Claims Detail totals are
  // not expected to tie out across the whole report. Per claimant they do — a Summary
  // row's Owed is exactly that claimant's in-range claims.
  //
  // The predicate is shared with calculateClaimsSummaryRows so the two can never
  // disagree about WHICH claims fall in the period (#6825).

  const stoplossClaims = useMemo(() => {
    if (stoplossClaimsLoading) return EMPTY_ARRAY;

    return stoplossClaimsRaw.filter((claim) => isClaimInReportingPeriod(claim, startDate, endDate));
  }, [stoplossClaimsRaw, startDate, endDate, stoplossClaimsLoading]);

  // ==================== FUNDED-DATE LOOKUP ====================
  // Reimbursements carry the Aegle Accel funded date only indirectly: the date
  // lives on the funding-request-grain stop-loss claim (aura_disbursed_at, US 5330).
  // Build claim_id → funded date so the reimbursement tab can resolve it via
  // reimbursement.stoploss_claim_id_ref (US 5330: funded date is
  // claim-grain, not per-reimbursement). Uses the unfiltered raw claims so funded
  // reimbursements outside the detail-table date window still resolve.

  const fundedDateByClaimId = useMemo(
    () =>
      stoplossClaimsRaw.reduce<Record<string, string>>((acc, claim) => {
        if (claim.aura_disbursed_at) acc[String(claim.id)] = claim.aura_disbursed_at;
        return acc;
      }, {}),
    [stoplossClaimsRaw]
  );

  // ==================== VENDOR / EXPORT FETCHES ====================

  const { data: carrierVendor } = useGetOne<Vendor>(
    ResourceName.VENDORS,
    { id: selectedPolicy?.vendor_id_ref ?? "" },
    { enabled: !!selectedPolicy?.vendor_id_ref }
  );

  const tpaVendorIds = selectedPolicy?.tpa_vendor_ids ?? [];
  const { data: tpaVendors = EMPTY_ARRAY } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: tpaVendorIds },
    { enabled: tpaVendorIds.length > 0 }
  );

  const rxVendorIds = selectedPolicy?.rx_vendor_ids ?? [];
  const { data: rxVendors = EMPTY_ARRAY } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: rxVendorIds },
    { enabled: rxVendorIds.length > 0 }
  );

  const { data: policyholderClient } = useGetOne<Client>(
    ResourceName.CLIENTS,
    { id: selectedPolicy?.client_id_ref ?? "" },
    { enabled: !!selectedPolicy?.client_id_ref }
  );

  // ==================== TRUNCATION ====================
  // Every feeding fetch asks for a single large page and the reporting-period filter
  // then runs client-side, so a client whose data exceeds a page size loses rows with
  // no error anywhere. The stop-loss fetch sorts created_datetime DESC, which means the
  // rows lost are the OLDEST — so a prior-period report is the one that quietly comes up
  // short, which is exactly the report #6823 exists to make trustworthy. Surface it.
  const truncatedSources = useMemo(() => {
    const sources: TruncatedSource[] = [];
    const check = (label: string, fetched: number, total: number | undefined, pageSize: number) => {
      if (total !== undefined && total > pageSize) sources.push({ label, fetched, total });
    };
    check("Claimants", claimants.length, claimantsTotal, CLAIMANTS_PAGE_SIZE);
    check("Stop-loss claims", stoplossClaimsRaw.length, stoplossClaimsTotal, STOPLOSS_CLAIMS_PAGE_SIZE);
    check("Reimbursements", reimbursements.length, reimbursementsTotal, REIMBURSEMENTS_PAGE_SIZE);
    return sources;
  }, [
    claimants.length,
    claimantsTotal,
    stoplossClaimsRaw.length,
    stoplossClaimsTotal,
    reimbursements.length,
    reimbursementsTotal,
  ]);

  // Name lookup over the FULL fetched claimant set — see the doc on the result field.
  const claimantNameById = useMemo(
    () => new Map(claimants.map((c) => [String(c.id), c.claimant_name ?? ""])),
    [claimants]
  );

  // ==================== CALCULATIONS ====================

  const prevSummaryRef = useRef<string>("");
  const prevWatchListRef = useRef<string>("");

  // Summary rows — claimants at or over their effective specific deductible (#6823)
  useEffect(() => {
    if (!clientId || !policyId || sharedDataLoading) {
      // Clear rows while loading — needed so a policy switch doesn't show the previous
      // policy's rows during the refetch (can't just `return` here). Keep the same
      // reference when already empty so this stays loop-proof: the root cause of #5168
      // (EMPTY_ARRAY for the useGetList defaults above) keeps deps stable, and this
      // idempotent reset is the second layer — even if a future dep churns, React bails
      // out instead of re-rendering on a fresh `[]`.
      setSummaryRows((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const rows = calculateClaimsSummaryRows({
      claimants,
      stoplossClaims: stoplossClaimsRaw,
      reimbursements,
      policyId,
      specificDeductible,
      startDate,
      endDate,
    });

    const json = JSON.stringify(rows);
    if (json !== prevSummaryRef.current) {
      prevSummaryRef.current = json;
      setSummaryRows(rows);
    }
  }, [
    claimants,
    stoplossClaimsRaw,
    reimbursements,
    clientId,
    policyId,
    specificDeductible,
    startDate,
    endDate,
    sharedDataLoading,
  ]);

  // Watch list rows — uses backend-computed snapshot totals (deduped)
  useEffect(() => {
    if (!clientId || !policyId || sharedDataLoading) {
      // Same layered reset as the summary effect above (clear stale rows on reload;
      // idempotent so it can't loop).
      setWatchListRows((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const rows = calculateWatchListRows({
      claimants,
      policyId,
      specificDeductible,
    });

    const json = JSON.stringify(rows);
    if (json !== prevWatchListRef.current) {
      prevWatchListRef.current = json;
      setWatchListRows(rows);
    }
  }, [claimants, clientId, policyId, specificDeductible, sharedDataLoading]);

  // ==================== RETURN ====================

  return {
    policies,
    policiesLoading,
    selectedPolicy,
    carrierMap,
    summaryRows,
    claimantNameById,
    watchListRows,
    stoplossClaims,
    stoplossClaimsLoading,
    reimbursements,
    fundedDateByClaimId,
    carrierVendor,
    tpaVendors,
    rxVendors,
    policyholderClient,
    sharedDataLoading,
    claimantsLoading,
    truncatedSources,
  };
};
